(() => {
  if (window.__collectionCatalogInitialized) return;
  window.__collectionCatalogInitialized = true;

  const SECTION_SELECTOR = '[data-collection-catalog]';
  const FILTER_LINK_SELECTOR = [
    'a.collection-catalog__tag-chip',
    'a.collection-catalog__availability',
    '.collection-catalog__filter-group a.collection-catalog__sidebar-link',
    '.collection-catalog__filter-group a.collection-catalog__filter-clear'
  ].join(', ');
  const PAGINATION_LINK_SELECTOR = [
    '.collection-catalog__pagination a.collection-catalog__pagination-page',
    '.collection-catalog__pagination a.collection-catalog__pagination-arrow'
  ].join(', ');
  const AJAX_LINK_SELECTOR = [
    FILTER_LINK_SELECTOR,
    PAGINATION_LINK_SELECTOR
  ].join(', ');
  const AJAX_FORM_SELECTOR = '[data-catalog-sort-form], .collection-catalog__price-form';
  const MOBILE_QUERY = window.matchMedia('(max-width: 989px)');
  const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');
  const requestControllers = new WeakMap();
  const animationTimers = new WeakMap();
  const priceRefreshTimers = new WeakMap();
  const sectionResponseCache = new Map();
  const CARD_ANIMATION_DURATION_MS = 420;
  const CARD_ANIMATION_STAGGER_MS = 45;
  const CARD_ANIMATION_MAX_STAGGER = 10;
  const PRICE_REFRESH_DEBOUNCE_MS = 180;
  const SECTION_RESPONSE_CACHE_LIMIT = 12;
  const SORT_LABEL_MAX_CHARS = 16;

  const getTextNode = (toggleButton) => toggleButton?.querySelector('.collection-catalog__mobile-filter-text');

  const getSectionId = (section) => {
    if (!section) return '';

    const explicitId = section.getAttribute('data-section-id');
    if (explicitId) return explicitId;

    const host = section.closest('[id^="shopify-section-"]');
    if (!host) return '';

    return host.id.replace('shopify-section-', '');
  };

  const setFilterPanelState = (section, isOpen) => {
    const panel = section.querySelector('[data-catalog-filter-panel]');
    const toggleButton = section.querySelector('[data-catalog-filter-toggle]');
    if (!panel || !toggleButton) return;

    panel.classList.toggle('is-open', isOpen);
    toggleButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

    const textNode = getTextNode(toggleButton);
    if (!textNode) return;

    const openLabel = toggleButton.getAttribute('data-open-label') || textNode.textContent || '';
    const closeLabel = toggleButton.getAttribute('data-close-label') || openLabel;
    textNode.textContent = isOpen ? closeLabel : openLabel;
  };

  const closeSortDropdown = (form) => {
    if (!form) return;
    const toggle = form.querySelector('[data-catalog-sort-toggle]');
    const panel = form.querySelector('[data-catalog-sort-panel]');

    form.classList.remove('is-open');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    if (panel) panel.hidden = true;
  };

  const openSortDropdown = (form) => {
    if (!form) return;
    const toggle = form.querySelector('[data-catalog-sort-toggle]');
    const panel = form.querySelector('[data-catalog-sort-panel]');

    form.classList.add('is-open');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    if (panel) panel.hidden = false;
  };

  const truncateSortLabel = (label) => {
    const normalizedLabel = String(label || '').trim();
    if (!normalizedLabel) return '';
    if (normalizedLabel.length <= SORT_LABEL_MAX_CHARS) return normalizedLabel;
    return `${normalizedLabel.slice(0, SORT_LABEL_MAX_CHARS)}...`;
  };

  const updateSelectedSortLabel = (form, label) => {
    const labelNode = form.querySelector('[data-catalog-sort-current]');
    if (labelNode && label) labelNode.textContent = truncateSortLabel(label);
  };

  const syncSortControls = (form, value) => {
    const hiddenInput = form.querySelector('[data-catalog-sort-input]');
    const nativeSelect = form.querySelector('[data-catalog-sort-native]');
    const options = form.querySelectorAll('[data-catalog-sort-option]');

    if (hiddenInput) hiddenInput.value = value;
    if (nativeSelect) nativeSelect.value = value;

    options.forEach((optionButton) => {
      const isActive = optionButton.getAttribute('data-sort-value') === value;
      optionButton.classList.toggle('is-active', isActive);
      if (isActive) {
        updateSelectedSortLabel(form, optionButton.getAttribute('data-sort-label'));
      }
    });
  };

  const closeAllSortsExcept = (currentForm) => {
    document.querySelectorAll(`${SECTION_SELECTOR} [data-catalog-sort-form].is-open`).forEach((form) => {
      if (form !== currentForm) closeSortDropdown(form);
    });
  };

  const parseIntSafe = (value, fallback = 0) => {
    const parsed = Number.parseInt(String(value), 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const formatPriceValue = (form, value) => {
    const normalizeCurrencyPart = (rawValue) =>
      (rawValue || '')
        .replace(/[\d\s.,\u00A0]/g, '')
        .trim();

    let prefix = normalizeCurrencyPart(form.getAttribute('data-price-currency-prefix'));
    let suffix = normalizeCurrencyPart(form.getAttribute('data-price-currency-suffix'));

    if (prefix && suffix && prefix === suffix) {
      prefix = '';
    }

    if (prefix && suffix) return `${prefix}${value} ${suffix}`;
    if (prefix) return `${prefix}${value}`;
    if (suffix) return `${value} ${suffix}`;
    return `${value} zł`;
  };

  const clearPriceRefreshTimer = (form) => {
    const timerId = priceRefreshTimers.get(form);
    if (!timerId) return;

    window.clearTimeout(timerId);
    priceRefreshTimers.delete(form);
  };

  const schedulePriceRefresh = (form) => {
    clearPriceRefreshTimer(form);

    const timerId = window.setTimeout(() => {
      const section = form.closest(SECTION_SELECTOR);
      if (!section || !getSectionId(section)) return;

      const nextUrl = buildUrlFromForm(form);
      refreshSection(section, nextUrl, { pushHistory: true });
    }, PRICE_REFRESH_DEBOUNCE_MS);

    priceRefreshTimers.set(form, timerId);
  };

  const syncPriceFormState = (form, changedHandle, shouldRefresh) => {
    if (!form) return;

    const slider = form.querySelector('[data-price-slider]');
    const minRange = form.querySelector('[data-price-range-min]');
    const maxRange = form.querySelector('[data-price-range-max]');
    const minHidden = form.querySelector('[data-price-hidden-min]');
    const maxHidden = form.querySelector('[data-price-hidden-max]');
    const minDisplay = form.querySelector('[data-price-display-min]');
    const maxDisplay = form.querySelector('[data-price-display-max]');

    if (!slider || !minRange || !maxRange || !minHidden || !maxHidden || !minDisplay || !maxDisplay) return;

    const minBound = parseIntSafe(slider.getAttribute('data-price-min'), 0);
    const maxBoundRaw = parseIntSafe(slider.getAttribute('data-price-max'), 0);
    const maxBound = Math.max(minBound, maxBoundRaw);

    let minValue = clamp(parseIntSafe(minRange.value, minBound), minBound, maxBound);
    let maxValue = clamp(parseIntSafe(maxRange.value, maxBound), minBound, maxBound);

    if (changedHandle === 'min' && minValue > maxValue) minValue = maxValue;
    if (changedHandle === 'max' && maxValue < minValue) maxValue = minValue;

    if (minValue > maxValue) minValue = maxValue;
    if (maxValue < minValue) maxValue = minValue;

    minRange.value = String(minValue);
    maxRange.value = String(maxValue);
    minDisplay.textContent = formatPriceValue(form, minValue);
    maxDisplay.textContent = formatPriceValue(form, maxValue);

    minHidden.value = minValue > minBound ? String(minValue) : '';
    maxHidden.value = maxValue < maxBound ? String(maxValue) : '';

    const total = Math.max(1, maxBound - minBound);
    const startPercent = ((minValue - minBound) / total) * 100;
    const endPercent = ((maxValue - minBound) / total) * 100;

    form.style.setProperty('--catalog-price-start', `${startPercent}%`);
    form.style.setProperty('--catalog-price-end', `${endPercent}%`);

    if (shouldRefresh) {
      schedulePriceRefresh(form);
    }
  };

  const setupPriceForms = (section) => {
    if (!section) return;

    section.querySelectorAll('[data-price-live-form]').forEach((form) => {
      if (form.dataset.priceReady === 'true') return;
      form.dataset.priceReady = 'true';
      syncPriceFormState(form, null, false);
    });
  };

  const setupSection = (section) => {
    if (!section || section.dataset.catalogReady === 'true') return;
    section.dataset.catalogReady = 'true';

    if (section.dataset.catalogCachePrimed !== 'true') {
      const sectionId = getSectionId(section);
      if (sectionId) {
        const currentRequestUrl = buildSectionRequestUrl(window.location.href, sectionId);
        cacheSectionResponse(currentRequestUrl.toString(), section.outerHTML);
        section.dataset.catalogCachePrimed = 'true';
      }
    }

    const sortForm = section.querySelector('[data-catalog-sort-form]');
    if (sortForm) {
      const hiddenInput = sortForm.querySelector('[data-catalog-sort-input]');
      if (hiddenInput) {
        syncSortControls(sortForm, hiddenInput.value);
      }
    }

    setupPriceForms(section);

    if (!MOBILE_QUERY.matches) {
      setFilterPanelState(section, false);
    }
  };

  const bootstrap = (scope = document) => {
    scope.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      setupSection(section);

      if (section.dataset.catalogInitialAnimated !== 'true') {
        animateGridCards(section);
        section.dataset.catalogInitialAnimated = 'true';
      }
    });
  };

  const buildSectionRequestUrl = (rawUrl, sectionId) => {
    const url = new URL(rawUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    return url;
  };

  const buildHistoryUrl = (rawUrl) => {
    const url = new URL(rawUrl, window.location.origin);
    url.searchParams.delete('section_id');
    return `${url.pathname}${url.search}${url.hash}`;
  };

  const parseSectionFromHtml = (html) => {
    const parser = new DOMParser();
    const documentFragment = parser.parseFromString(html, 'text/html');
    return documentFragment.querySelector(SECTION_SELECTOR);
  };

  const getCachedSectionResponse = (cacheKey) => {
    if (!sectionResponseCache.has(cacheKey)) return '';

    const cachedHtml = sectionResponseCache.get(cacheKey);
    sectionResponseCache.delete(cacheKey);
    sectionResponseCache.set(cacheKey, cachedHtml);
    return cachedHtml;
  };

  const cacheSectionResponse = (cacheKey, html) => {
    if (!cacheKey || !html) return;

    sectionResponseCache.delete(cacheKey);
    sectionResponseCache.set(cacheKey, html);

    while (sectionResponseCache.size > SECTION_RESPONSE_CACHE_LIMIT) {
      const oldestCacheKey = sectionResponseCache.keys().next().value;
      sectionResponseCache.delete(oldestCacheKey);
    }
  };

  const removeSectionQuickAddModals = (sectionId) => {
    if (!sectionId) return;

    document.querySelectorAll('body > quick-add-modal[data-section]').forEach((modal) => {
      if (modal.dataset.section === sectionId) modal.remove();
    });
  };

  const abortSectionRequest = (section) => {
    const existingController = requestControllers.get(section);
    if (!existingController) return;

    existingController.abort();
    requestControllers.delete(section);
  };

  const setSectionLoadingState = (section, isLoading) => {
    section.classList.toggle('is-loading', isLoading);
    section.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  };

  const clearGridAnimation = (section) => {
    const existingTimer = animationTimers.get(section);
    if (existingTimer) {
      window.clearTimeout(existingTimer);
      animationTimers.delete(section);
    }

    section.classList.remove('is-animating');

    section.querySelectorAll('.collection-catalog__grid .perfumiarz-products__card').forEach((card) => {
      card.classList.remove('is-entering');
      card.style.removeProperty('--catalog-stagger-index');
    });
  };

  const animateGridCards = (section) => {
    const grid = section.querySelector('.collection-catalog__grid');
    if (!grid || REDUCED_MOTION_QUERY.matches) return;

    const cards = Array.from(grid.querySelectorAll('.perfumiarz-products__card'));
    if (!cards.length) return;

    clearGridAnimation(section);
    section.classList.add('is-animating');

    cards.forEach((card, index) => {
      card.style.setProperty('--catalog-stagger-index', String(Math.min(index, CARD_ANIMATION_MAX_STAGGER)));
      card.classList.add('is-entering');
    });

    const raf = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 16));
    raf(() => {
      const raf2 = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 16));
      raf2(() => {
        cards.forEach((card) => {
          card.classList.remove('is-entering');
        });
      });
    });

    const totalDuration =
      CARD_ANIMATION_DURATION_MS + Math.min(cards.length - 1, CARD_ANIMATION_MAX_STAGGER) * CARD_ANIMATION_STAGGER_MS + 60;

    const cleanupTimer = window.setTimeout(() => {
      clearGridAnimation(section);
    }, totalDuration);

    animationTimers.set(section, cleanupTimer);
  };

  const scrollToSectionTop = (section) => {
    if (!section) return;

    const sectionTop = section.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({
      top: Math.max(0, Math.round(sectionTop)),
      behavior: REDUCED_MOTION_QUERY.matches ? 'auto' : 'smooth'
    });
  };

  const refreshSection = async (section, nextUrl, { pushHistory = true, replaceHistory = false, scrollToTop = false } = {}) => {
    const sectionId = getSectionId(section);
    if (!sectionId) {
      window.location.assign(nextUrl.toString());
      return;
    }

    abortSectionRequest(section);

    const requestController = new AbortController();
    requestControllers.set(section, requestController);

    const keepFiltersOpen = MOBILE_QUERY.matches && Boolean(section.querySelector('[data-catalog-filter-panel]')?.classList.contains('is-open'));

    try {
      setSectionLoadingState(section, true);

      const requestUrl = buildSectionRequestUrl(nextUrl, sectionId);
      const cacheKey = requestUrl.toString();
      let responseHtml = getCachedSectionResponse(cacheKey);

      if (!responseHtml) {
        const response = await fetch(cacheKey, {
          method: 'GET',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          },
          signal: requestController.signal
        });

        if (!response.ok) throw new Error(`Collection catalog request failed: ${response.status}`);

        responseHtml = await response.text();
        cacheSectionResponse(cacheKey, responseHtml);
      }

      const nextSection = parseSectionFromHtml(responseHtml);
      if (!nextSection) throw new Error('Collection catalog section not found in response');

      removeSectionQuickAddModals(sectionId);
      section.innerHTML = nextSection.innerHTML;
      section.dataset.catalogReady = 'false';
      setupSection(section);

      if (scrollToTop) {
        scrollToSectionTop(section);
      }

      if (keepFiltersOpen && MOBILE_QUERY.matches) {
        setFilterPanelState(section, true);
      }

      section.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));

      if (pushHistory) {
        const historyUrl = buildHistoryUrl(nextUrl);
        if (replaceHistory) {
          window.history.replaceState({ collectionCatalog: true }, '', historyUrl);
        } else {
          window.history.pushState({ collectionCatalog: true }, '', historyUrl);
        }
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      window.location.assign(nextUrl.toString());
    } finally {
      if (requestControllers.get(section) === requestController) {
        requestControllers.delete(section);
      }
      setSectionLoadingState(section, false);
    }
  };

  const shouldHandleAjaxLink = (event, link, section) => {
    if (!link || !section) return false;
    if (event.defaultPrevented) return false;
    if (event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (link.target && link.target !== '_self') return false;
    if (link.hasAttribute('download')) return false;

    const href = link.getAttribute('href');
    if (!href || href.startsWith('#')) return false;

    const destination = new URL(link.href, window.location.origin);
    if (destination.origin !== window.location.origin) return false;

    return Boolean(getSectionId(section));
  };

  const applyOptimisticFilterState = (link) => {
    if (!link || !link.matches(FILTER_LINK_SELECTOR)) return;

    const purposeFilter = link.closest('[data-catalog-purpose-filter]');
    if (purposeFilter) {
      const isClearLink = link.classList.contains('collection-catalog__filter-clear');
      const wasActive = link.classList.contains('is-active');

      purposeFilter
        .querySelectorAll('a.collection-catalog__sidebar-link, a.collection-catalog__filter-clear')
        .forEach((purposeLink) => {
          purposeLink.classList.remove('is-active', 'is-pending');
          purposeLink.setAttribute('aria-pressed', 'false');
        });

      if (!isClearLink && !wasActive) {
        link.classList.add('is-active');
        link.setAttribute('aria-pressed', 'true');
      }

      link.classList.add('is-pending');
      return;
    }

    if (link.classList.contains('collection-catalog__filter-clear')) {
      link.classList.add('is-pending');
      return;
    }

    const check = link.querySelector('.collection-catalog__check');
    const isActive = check ? check.classList.contains('is-active') : link.classList.contains('is-active');
    const nextActive = !isActive;

    link.classList.toggle('is-active', nextActive);
    link.classList.add('is-pending');
    link.setAttribute('aria-pressed', nextActive ? 'true' : 'false');

    if (check) {
      check.classList.toggle('is-active', nextActive);
    }
  };

  const buildUrlFromForm = (form) => {
    const action = form.getAttribute('action') || window.location.pathname;
    const url = new URL(action, window.location.origin);
    const formData = new FormData(form);

    formData.forEach((value, key) => {
      if (value === '' || value == null) return;
      url.searchParams.append(key, String(value));
    });

    return url;
  };

  document.addEventListener('click', (event) => {
    const sortToggle = event.target.closest('[data-catalog-sort-toggle]');
    if (sortToggle) {
      const form = sortToggle.closest('[data-catalog-sort-form]');
      if (!form) return;
      event.preventDefault();

      const shouldOpen = !form.classList.contains('is-open');
      closeAllSortsExcept(form);
      if (shouldOpen) {
        openSortDropdown(form);
      } else {
        closeSortDropdown(form);
      }
      return;
    }

    const sortOption = event.target.closest('[data-catalog-sort-option]');
    if (sortOption) {
      const form = sortOption.closest('[data-catalog-sort-form]');
      if (!form) return;
      event.preventDefault();

      const selectedValue = sortOption.getAttribute('data-sort-value');
      if (!selectedValue) return;

      syncSortControls(form, selectedValue);
      closeSortDropdown(form);
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
      return;
    }

    const filterToggle = event.target.closest('[data-catalog-filter-toggle]');
    if (filterToggle) {
      const section = filterToggle.closest(SECTION_SELECTOR);
      if (!section) return;
      event.preventDefault();

      const panel = section.querySelector('[data-catalog-filter-panel]');
      const willOpen = !panel?.classList.contains('is-open');
      setFilterPanelState(section, Boolean(willOpen));
      return;
    }

    const ajaxLink = event.target.closest(AJAX_LINK_SELECTOR);
    if (ajaxLink) {
      const section = ajaxLink.closest(SECTION_SELECTOR);
      if (!shouldHandleAjaxLink(event, ajaxLink, section)) return;

      event.preventDefault();
      if (ajaxLink.matches(FILTER_LINK_SELECTOR) && ajaxLink.classList.contains('is-pending')) return;
      applyOptimisticFilterState(ajaxLink);
      refreshSection(section, new URL(ajaxLink.href, window.location.origin), {
        pushHistory: true,
        scrollToTop: ajaxLink.matches(PAGINATION_LINK_SELECTOR)
      });
      return;
    }

    const clickedInsideSort = event.target.closest('[data-catalog-sort-form]');
    if (!clickedInsideSort) {
      closeAllSortsExcept(null);
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest(AJAX_FORM_SELECTOR);
    if (!form) return;

    const section = form.closest(SECTION_SELECTOR);
    if (!section || !getSectionId(section)) return;

    event.preventDefault();
    const nextUrl = buildUrlFromForm(form);
    refreshSection(section, nextUrl, { pushHistory: true });
  });

  document.addEventListener('input', (event) => {
    const rangeInput = event.target.closest('[data-price-range-min], [data-price-range-max]');
    if (!rangeInput) return;

    const form = rangeInput.closest('[data-price-live-form]');
    if (!form) return;

    const changedHandle = rangeInput.hasAttribute('data-price-range-min') ? 'min' : 'max';
    syncPriceFormState(form, changedHandle, true);
  });

  document.addEventListener('change', (event) => {
    const rangeInput = event.target.closest('[data-price-range-min], [data-price-range-max]');
    if (!rangeInput) return;

    const form = rangeInput.closest('[data-price-live-form]');
    if (!form) return;

    const changedHandle = rangeInput.hasAttribute('data-price-range-min') ? 'min' : 'max';
    syncPriceFormState(form, changedHandle, true);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    document.querySelectorAll(`${SECTION_SELECTOR} [data-catalog-sort-form].is-open`).forEach((form) => {
      closeSortDropdown(form);
    });
  });

  const handleBreakpointChange = () => {
    if (MOBILE_QUERY.matches) return;

    document.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      setFilterPanelState(section, false);
      section.querySelectorAll('[data-catalog-sort-form].is-open').forEach((form) => closeSortDropdown(form));
    });
  };

  if (typeof MOBILE_QUERY.addEventListener === 'function') {
    MOBILE_QUERY.addEventListener('change', handleBreakpointChange);
  } else if (typeof MOBILE_QUERY.addListener === 'function') {
    MOBILE_QUERY.addListener(handleBreakpointChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bootstrap(document));
  } else {
    bootstrap(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    bootstrap(event.target);
  });

  window.addEventListener('popstate', () => {
    const section = document.querySelector(SECTION_SELECTOR);
    if (!section || !getSectionId(section)) return;

    refreshSection(section, new URL(window.location.href), {
      pushHistory: false,
      replaceHistory: true
    });
  });
})();
